import React from 'react';
import { useAuthStore } from './features/auth/authStore';

export const TestAdminAccess: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();

  const requiredRoles = ['SuperAdmin', 'SUPER_ADMIN'];
  const hasRequiredRole = user?.roles?.some(role => requiredRoles.includes(role)) || false;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🔍 Admin Access Test</h1>
        
        <div className="grid gap-6">
          {/* Auth Store Status */}
          <div className="p-6 rounded-lg border-2 bg-blue-50 border-blue-200">
            <h2 className="text-xl font-semibold mb-4">Auth Store Status</h2>
            <p><strong>Store Active:</strong> ✅ Yes (no persistence)</p>
          </div>

          {/* Authentication Status */}
          <div className={`p-6 rounded-lg border-2 ${isAuthenticated ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h2 className="text-xl font-semibold mb-4">Authentication Status</h2>
            <p><strong>Is Authenticated:</strong> {isAuthenticated ? '✅ Yes' : '❌ No'}</p>
            <p><strong>User Present:</strong> {user ? '✅ Yes' : '❌ No'}</p>
          </div>

          {/* User Information */}
          <div className="p-6 rounded-lg border-2 bg-blue-50 border-blue-200">
            <h2 className="text-xl font-semibold mb-4">User Information</h2>
            {user ? (
              <>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Name:</strong> {user.firstName} {user.lastName}</p>
                <p><strong>Roles:</strong> {JSON.stringify(user.roles)}</p>
                <p><strong>Status:</strong> {user.status}</p>
                <p><strong>Tenant ID:</strong> {user.tenantId || 'null (SuperAdmin)'}</p>
              </>
            ) : (
              <p className="text-red-600">❌ No user data available</p>
            )}
          </div>

          {/* Role Validation */}
          <div className={`p-6 rounded-lg border-2 ${hasRequiredRole ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h2 className="text-xl font-semibold mb-4">Role Validation</h2>
            <p><strong>Required Roles:</strong> {JSON.stringify(requiredRoles)}</p>
            <p><strong>User Roles:</strong> {JSON.stringify(user?.roles || [])}</p>
            <p><strong>Has Required Role:</strong> {hasRequiredRole ? '✅ Yes' : '❌ No'}</p>
            
            <div className="mt-4">
              <h3 className="font-semibold">Detailed Check:</h3>
              <ul className="list-disc list-inside mt-2">
                {requiredRoles.map(role => (
                  <li key={role}>
                    "{role}": {user?.roles?.includes(role) ? '✅ Found' : '❌ Not found'}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Access Decision */}
          <div className={`p-6 rounded-lg border-2 ${hasRequiredRole && isAuthenticated && user ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h2 className="text-xl font-semibold mb-4">Access Decision</h2>
            <p><strong>Should Allow Access:</strong> {hasRequiredRole && isAuthenticated && user ? '✅ YES' : '❌ NO'}</p>
            
            <div className="mt-4">
              <h3 className="font-semibold">Conditions:</h3>
              <ul className="list-disc list-inside mt-2">
                <li>Is Authenticated: {isAuthenticated ? '✅' : '❌'}</li>
                <li>User Present: {user ? '✅' : '❌'}</li>
                <li>Has Required Role: {hasRequiredRole ? '✅' : '❌'}</li>
              </ul>
            </div>
          </div>

          {/* Raw Auth Store Data */}
          <div className="p-6 rounded-lg border-2 bg-gray-50 border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Raw Auth Store Data</h2>
            <pre className="text-sm overflow-x-auto">
              {JSON.stringify({ isAuthenticated, user }, null, 2)}
            </pre>
          </div>

          {/* LocalStorage Data */}
          <div className="p-6 rounded-lg border-2 bg-gray-50 border-gray-200">
            <h2 className="text-xl font-semibold mb-4">LocalStorage Data</h2>
            <pre className="text-sm overflow-x-auto">
              {localStorage.getItem('erpca-auth') || 'No data found'}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};
